import { useCallback, useMemo, useRef, useState } from 'react';
import { M_REGULAR_COLLECTION_URLS } from 'constants/apiConstants';
import { DEFAULT_PAGE_SIZE } from 'constants/appConstants';
import { useFetchGCollection } from 'hooks/useFetchGCollection';
import { useFetchMCollection } from 'hooks/useFetchMCollection';
import { useMSearchCollection } from 'hooks/useMSearchCollection';
import { useSearchDebounce } from 'hooks/useSearchDebounce';
import { CatalogueGBase, CatalogueMBase } from 'interfaces/ICatalogue';
import { MSearchFilter } from 'interfaces/IMasterDb';
import { filterByNameTerm } from 'utils/catalogueMergeUtils';

interface UseCatalogueDropdownOptions<
  GItem extends CatalogueGBase,
  MItem extends CatalogueMBase,
  Merged extends CatalogueGBase,
> {
  gOrgId: string | null;
  mOrgId: string | null;
  gCollectionKey: string;
  mCollectionKey: keyof typeof M_REGULAR_COLLECTION_URLS | null;
  gSearchCollectionName: string;
  gQueryKey: unknown[];
  mQueryKey: unknown[];
  searchQueryKey: unknown[];
  // Extra AND conditions for the server-side MSearch only (browse is unaffected).
  searchFilters?: MSearchFilter[][];
  mergeItems: (gItems: GItem[], mItems: MItem[]) => Merged[];
  selectedId?: string | null;
  gPageSize?: number;
}

export const useCatalogueDropdown = <
  GItem extends CatalogueGBase,
  MItem extends CatalogueMBase,
  Merged extends CatalogueGBase,
>({
  gOrgId,
  mOrgId,
  gCollectionKey,
  mCollectionKey,
  gSearchCollectionName,
  gQueryKey,
  mQueryKey,
  searchQueryKey,
  searchFilters,
  mergeItems,
  selectedId,
  gPageSize = DEFAULT_PAGE_SIZE,
}: UseCatalogueDropdownOptions<GItem, MItem, Merged>) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useSearchDebounce(searchTerm);

  const mergeItemsRef = useRef(mergeItems);
  mergeItemsRef.current = mergeItems;

  const {
    data: gData,
    isFetching: isGFetching,
    isFetchingNextPage: isGPaginating,
    hasNextPage: gHasNextPage,
    fetchNextPage: fetchNextGPage,
    isFetchNextPageError: gHasFetchNextPageError,
    status: gStatus,
  } = useFetchGCollection<GItem>(gCollectionKey, gOrgId, gQueryKey, gPageSize);

  const { data: mData } = useFetchMCollection<MItem>(
    mCollectionKey,
    mOrgId,
    mQueryKey
  );

  const {
    data: searchData,
    isFetching: isSearchFetching,
    isFetchingNextPage: isSearchPaginating,
    hasNextPage: searchHasNextPage,
    fetchNextPage: fetchNextSearchPage,
    isFetchNextPageError: searchHasFetchNextPageError,
  } = useMSearchCollection<GItem>(
    gSearchCollectionName,
    gOrgId,
    debouncedSearchTerm,
    searchQueryKey,
    searchFilters
  );

  const allGItems = useMemo(() => gData?.pages.flat() ?? [], [gData?.pages]);

  const mergedItems = useMemo(
    () => mergeItemsRef.current(allGItems, mData ?? []),
    [allGItems, mData]
  );

  const gGlobalIdSet = useMemo(
    () => new Set(allGItems.map((g) => g.globalId)),
    [allGItems]
  );

  // M-only items deferred until G pagination reaches their alphabetical position.
  // The already-selected item is always included regardless.
  const visibleItems = useMemo(() => {
    if (!gOrgId) return mergedItems;
    if (!gHasNextPage && gStatus !== 'pending') return mergedItems;
    if (allGItems.length === 0) {
      return selectedId
        ? mergedItems.filter((item) => item.globalId === selectedId)
        : [];
    }
    const lastGName = allGItems[allGItems.length - 1].name;
    return mergedItems.filter(
      (item) =>
        gGlobalIdSet.has(item.globalId) ||
        item.name.localeCompare(lastGName) <= 0 ||
        item.globalId === selectedId
    );
  }, [
    mergedItems,
    gHasNextPage,
    gStatus,
    allGItems,
    gOrgId,
    gGlobalIdSet,
    selectedId,
  ]);

  const gSearchItems = useMemo(
    () => searchData?.pages.flatMap((page) => page.data) ?? [],
    [searchData]
  );

  // Combines GC search results with a client-side MC name filter, via the same
  // mergeItems fn used for browsing, so MC-only matches surface and MC still
  // overrides GC on globalId collision (fixes OP-2088: search used to be GC-only).
  const mSearchMatches = useMemo(
    () => filterByNameTerm(mData ?? [], debouncedSearchTerm),
    [mData, debouncedSearchTerm]
  );

  const searchItems = useMemo(
    () => mergeItemsRef.current(gSearchItems, mSearchMatches),
    [gSearchItems, mSearchMatches]
  );

  // Immediate client-side filter on all merged browse data — used as a preview
  // while MSearch is loading (non-debounced so results appear instantly on type).
  const localFilteredBrowse = useMemo(
    () => filterByNameTerm(mergedItems, searchTerm),
    [mergedItems, searchTerm]
  );

  // Union of browse-filtered items and MSearch results, deduped by globalId and
  // sorted alphabetically. MSearch results take priority on collision since they
  // may carry fresher M-override data than the paginated browse snapshot.
  const mergedWithSearch = useMemo(() => {
    if (!searchData) return localFilteredBrowse;
    const unionMap = new Map<string, Merged>();
    for (const item of localFilteredBrowse) {
      unionMap.set(item.globalId, item);
    }
    for (const item of searchItems) {
      unionMap.set(item.globalId, item);
    }
    return Array.from(unionMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [localFilteredBrowse, searchItems, searchData]);

  const isSearchMode = searchTerm !== '';

  // True immediately when user types (before debounce fires), sustains through search fetch.
  // In browse mode: true only during initial load (not pagination — that is isPaginating).
  const isLoading = isSearchMode
    ? isSearchFetching || searchTerm !== debouncedSearchTerm
    : isGFetching && allGItems.length === 0;

  const onSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  return {
    // In search mode: show local filtered browse data immediately (before MSearch returns),
    // then switch to the merged result once MSearch data is available.
    items: isSearchMode ? mergedWithSearch : visibleItems,
    allMergedItems: mergedItems,
    searchItems,
    isLoading,
    isSearchFetching,
    isPaginating: isSearchMode ? isSearchPaginating : isGPaginating,
    // hasNextPage only meaningful once MSearch has returned; local browse preview is not paginated.
    hasNextPage: isSearchMode ? (searchData ? searchHasNextPage : false) : gHasNextPage,
    fetchNextPage: isSearchMode ? fetchNextSearchPage : fetchNextGPage,
    hasFetchNextPageError: isSearchMode
      ? searchHasFetchNextPageError
      : gHasFetchNextPageError,
    searchTerm,
    onSearch,
    clearSearch,
  };
};
